import { type QueryKey, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { isObject, isUndefined } from 'lodash';
import { useRouter } from 'next/router';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { type Board, type Clip, ViewTypeName } from '@air/api/types';
import { type Upload, UploadStatus } from '@air/redux-uploader';

import { GALLERY_ASSETS, GALLERY_BOARDS, GALLERY_FILES, SEARCH_TOTALS } from '~/constants/react-query-keys';
import { useAppliedSortField } from '~/hooks/search/useAppliedSortField';
import { useSearchParams } from '~/hooks/search/useSearchParams';
import { useRegisterClipEvents } from '~/hooks/useRegisterClipEvents';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { usePrivateSearchContext } from '~/providers/PrivateSearchProvider';
import { uploadsForCurrentBoardSelector } from '~/store/centralizedBoard/selectors';
import { currentViewTypeNameSelector } from '~/store/configViews/selectors';
import { useGalleryViewItems } from '~/swr-hooks/gallery/galleryView/useGalleryViewItems';
import { usePrivateBoardsFetcher } from '~/swr-hooks/gallery/galleryView/usePrivateBoardsFetcher';
import {
  galleryViewDataToUpdateMediaPlayer,
  getAssetFromAssetsData,
  privateClipsFetcher,
  privateFilesFetcher,
} from '~/swr-hooks/gallery/galleryView/utils';
import { defaultAssetsData, defaultBoardsData, type UseGalleryViewItemsReturn } from '~/swr-hooks/gallery/types';
import { useCanShowAssets, useCanShowBoards, useCanShowFiles } from '~/swr-hooks/gallery/useCanShow';
import { usePrivateMediaPlayerDataUpdate } from '~/swr-hooks/mediaPlayer/usePrivateMediaPlayerData';
import { type UseBoardSearchParamsReturn, useBoardSearchParams } from '~/swr-hooks/search/useBoardSearchParams';
import { type UseClipSearchParamsReturn, useClipSearchParams } from '~/swr-hooks/search/useClipSearchParams';
import { usePrivateSearchTotalsQueryKey } from '~/swr-hooks/search/usePrivateSearchTotals';
import { type SearchTotals } from '~/types/SearchTotals';
import { useAirSelector } from '~/utils/ReduxUtils';

export type UsePrivateGalleryViewItemsProps = {
  shouldUpdateMediaPlayer?: boolean;
  showBoards: boolean;
  boardsExpanded: boolean;
  assetsExpanded: boolean;
  showUploads: boolean;
  semantic?: boolean;
  hybrid?: boolean;
  includeCoverImage?: boolean;
  includeDirectDescendantCount?: boolean;
  includeDirectDescendantBoardCount?: boolean;
};

export type UsePrivateGalleryViewItemsReturn = {
  data: UseGalleryViewItemsReturn<Board, Clip>['data'] & { uploads: Upload[] };
} & UseGalleryViewItemsReturn<Board, Clip>;

const emptyUploadsArray: Upload[] = [];

export type PrivateGalleryViewItemsCacheKey = [typeof GALLERY_BOARDS, PrivateGalleryViewItemsCacheParams];

export type PrivateGalleryViewItemsCacheParams = UseClipSearchParamsReturn &
  UseBoardSearchParamsReturn & {
    canShowBoards: boolean;
    canShowFiles: boolean;
    canShowAssets: boolean;
    sortFieldName: string;
    sortDirection: string;
    basePathName: string;
    currentWorkspaceId: string | undefined;
  };

export const isPrivateGalleryViewItemsQueryKey = (key: QueryKey): key is PrivateGalleryViewItemsCacheKey => {
  return Array.isArray(key) && key[0] === GALLERY_BOARDS && isObject(key[1]);
};

// biome-ignore lint/plugin: pre-existing function declaration; out of scope for this selector swap
export function usePrivateGalleryViewItems({
  shouldUpdateMediaPlayer = true,
  showBoards,
  boardsExpanded,
  assetsExpanded,
  showUploads = true,
  semantic = false,
  hybrid = false,
  includeCoverImage,
  includeDirectDescendantCount,
  includeDirectDescendantBoardCount,
}: UsePrivateGalleryViewItemsProps): UsePrivateGalleryViewItemsReturn {
  const { privateBoardsFetcher } = usePrivateBoardsFetcher();
  const { updatePrivateMediaPlayerData } = usePrivateMediaPlayerDataUpdate();
  const { appliedSortFieldDirection: sortDirection, appliedSortFieldName: sortFieldName } = useAppliedSortField();
  const clipsFetchParams = useClipSearchParams({ semantic, hybrid });
  const boardsFetchParams = useBoardSearchParams({
    includeCoverImage,
    includeDirectDescendantCount,
    includeDirectDescendantBoardCount,
  });
  const currentViewTypeName = useSelector(currentViewTypeNameSelector);
  const { pathname } = useRouter();
  const basePathName = pathname.split('/')[1];

  const { search } = useSearchParams();
  const { searchType } = usePrivateSearchContext();
  const nonAssetsEnabled = useMemo(
    () => searchType === 'keyword' || (searchType === 'smart' && isUndefined(search)),
    [searchType, search],
  );

  const uploads = useAirSelector(showUploads ? uploadsForCurrentBoardSelector : () => emptyUploadsArray);
  const { canShowAssets } = useCanShowAssets();
  const { canShowFiles } = useCanShowFiles();
  const { currentWorkspace } = useCurrentWorkspace();
  const queryClient = useQueryClient();
  const { privateSearchTotalKey } = usePrivateSearchTotalsQueryKey();

  /**
   * The reason there are 2 "show boards" checks is because _canShowBoards is used to determine if we should
   * show boards from user-applied logic (filters, flattened view, etc.). showBoards comes from higher up
   * (at the page level - like in AllContent) and is a boolean we (the product) use to determine if we should
   * show boards.
   */
  const { canShowBoards: _canShowBoards } = useCanShowBoards();
  const canShowBoards = showBoards && _canShowBoards;

  const dataCacheParams = useMemo<PrivateGalleryViewItemsCacheParams>(() => {
    return {
      ...clipsFetchParams,
      ...boardsFetchParams,
      assetsExpanded,
      basePathName,
      boardsExpanded,
      canShowAssets,
      canShowBoards,
      canShowFiles,
      currentWorkspaceId: currentWorkspace?.id,
      sortDirection,
      sortFieldName,
    };
  }, [
    basePathName,
    clipsFetchParams,
    boardsFetchParams,
    canShowBoards,
    canShowFiles,
    canShowAssets,
    sortFieldName,
    sortDirection,
    currentWorkspace?.id,
    assetsExpanded,
    boardsExpanded,
  ]);

  const {
    isLoading: areBoardsLoading,
    data: boardsData,
    fetchNextPage: loadNextBoardPage,
    isFetchingNextPage: areBoardsValidating,
    hasNextPage: hasMoreBoards,
  } = useInfiniteQuery({
    queryKey: [GALLERY_BOARDS, dataCacheParams],
    queryFn: canShowBoards
      ? ({ pageParam }) =>
          privateBoardsFetcher({
            params: boardsFetchParams,
            sortFieldName,
            sortDirection,
            cursor: pageParam || null,
          })
      : () => defaultBoardsData,
    enabled: ViewTypeName.gallery === currentViewTypeName && !!currentWorkspace?.id && nonAssetsEnabled,
    getNextPageParam: (lastPage) => lastPage?.pagination?.cursor,
    initialPageParam: '',
  });

  const assetsParams = {
    params: clipsFetchParams,
    sortDirection,
    sortFieldName,
  };

  const areAllBoardsLoaded =
    !!boardsData && !!currentViewTypeName && (!canShowBoards || !hasMoreBoards || !boardsExpanded);

  const {
    data: clipsData,
    fetchNextPage: loadNextClipsPage,
    isFetchingNextPage: areClipsValidating,
    isLoading: areClipsLoading,
    hasNextPage: hasMoreClips,
  } = useInfiniteQuery({
    queryKey: [GALLERY_ASSETS, dataCacheParams],
    queryFn:
      canShowAssets && currentWorkspace
        ? ({ pageParam }) =>
            privateClipsFetcher({
              ...assetsParams,
              cursor: pageParam || null,
              workspaceId: currentWorkspace.id,
            })
        : () => defaultAssetsData,
    enabled: ({ state: { data } }) => {
      return !data || (areAllBoardsLoaded && !!currentWorkspace?.id);
    },
    getNextPageParam: (lastPage) => lastPage?.pagination?.cursor,
    initialPageParam: '',
  });

  const areAllClipsLoaded = !!clipsData && (!canShowAssets || !hasMoreClips || !assetsExpanded);

  useRegisterClipEvents({
    clips: getAssetFromAssetsData(clipsData?.pages),
    workspaceId: currentWorkspace?.id,
  });

  const {
    data: filesData,
    fetchNextPage: loadNextFilesPage,
    isFetchingNextPage: areFilesValidating,
    isLoading: areFilesLoading,
    hasNextPage: hasMoreFiles,
  } = useInfiniteQuery({
    queryKey: [GALLERY_FILES, dataCacheParams],
    queryFn:
      canShowFiles && currentWorkspace
        ? ({ pageParam }) =>
            privateFilesFetcher({
              ...assetsParams,
              cursor: pageParam || null,
              workspaceId: currentWorkspace.id,
            })
        : () => defaultAssetsData,
    enabled: ({ state: { data } }) =>
      (!data && nonAssetsEnabled) ||
      (areAllBoardsLoaded && areAllClipsLoaded && !!currentWorkspace?.id && nonAssetsEnabled),
    getNextPageParam: (lastPage) => lastPage?.pagination?.cursor,
    initialPageParam: '',
  });

  const itemsData = useGalleryViewItems({
    boards: {
      isValidating: areBoardsLoading || areBoardsValidating || !currentViewTypeName,
      data: boardsData?.pages,
      loadNextPage: loadNextBoardPage,
      isVisible: canShowBoards,
      hasMore: !!hasMoreBoards,
    },
    clips: {
      isValidating: areClipsLoading || areClipsValidating || !currentViewTypeName,
      data: clipsData?.pages,
      loadNextPage: loadNextClipsPage,
      isVisible: canShowAssets,
      hasMore: !!hasMoreClips,
    },
    files: {
      isValidating: areFilesValidating || areFilesLoading || !currentViewTypeName,
      data: filesData?.pages || [],
      loadNextPage: loadNextFilesPage,
      isVisible: canShowFiles,
      hasMore: !!hasMoreFiles,
    },
  });

  useEffect(() => {
    if (shouldUpdateMediaPlayer) {
      updatePrivateMediaPlayerData(galleryViewDataToUpdateMediaPlayer(itemsData));
    }
  }, [itemsData, updatePrivateMediaPlayerData, shouldUpdateMediaPlayer]);

  useEffect(() => {
    queryClient.setQueryData<SearchTotals>([SEARCH_TOTALS, privateSearchTotalKey], {
      assetCount: itemsData.data.clips.total,
      boardCount: itemsData.data.boards.total,
      fileCount: itemsData.data.files.total,
      total: itemsData.data.boards.total + itemsData.data.clips.total + itemsData.data.files.total,
    });
  }, [itemsData, privateSearchTotalKey, queryClient]);

  /**
   * Only uploads still in flight can make a board look non-empty. A completed one never decides it:
   * if its clip has landed in the gallery data then the clips already say the board has content, and
   * if it hasn't, `useGalleryAssets` hides the card as a zombie. Counting completed uploads meant a
   * board stayed non-empty for the rest of the session after its uploaded asset was deleted or
   * removed, rendering an empty grid where an empty state belonged. The uploads handed downstream
   * still include the completed ones, since the sections do their own matching against real clips.
   */
  const hasPendingUploads = useMemo(() => uploads.some(({ status }) => status !== UploadStatus.completed), [uploads]);

  return useMemo(
    () => ({
      ...itemsData,
      data: {
        ...itemsData.data,
        uploads,
      },
      isEmpty: itemsData.isEmpty && !hasPendingUploads,
    }),
    [hasPendingUploads, itemsData, uploads],
  );
}
